import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Download,
  KeyRound,
  Link as LinkIcon,
  Loader2,
  MailCheck,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config";
import { apiFetch } from "../../utils/api";

const DEFAULT_NOTIFICATIONS = {
  emailOrders: true,
  emailAlerts: true,
  weeklySummary: false,
};

export const AccountSettings = () => {
  const {
    hasAllegroConnected,
    setHasAllegroConnected,
    authFetch,
    authToken,
    AUTH_SCHEME,
  } = useAuth();

  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationMessage, setIntegrationMessage] = useState(null);
  const [importingData, setImportingData] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [allegroAccounts, setAllegroAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [primaryUpdatingId, setPrimaryUpdatingId] = useState(null);
  const [disconnectingAccountId, setDisconnectingAccountId] = useState(null);

  const resolveAccountLabel = useCallback((account) => {
    if (!account) return "Allegro account";
    return account.label || account.allegro_login || `Allegro account #${account.id}`;
  }, []);

  const formatTimestamp = (value) => {
    if (!value) return "Unknown";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const fetchAllegroAccounts = useCallback(async () => {
    if (!authToken) {
      setAllegroAccounts([]);
      setHasAllegroConnected(false);
      return;
    }
    setAccountsLoading(true);
    try {
      const resp = await authFetch(`${API_BASE_URL}/api/allegro/accounts/`, {
        headers: { Accept: "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data.detail || data.message || `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      const accounts = Array.isArray(data.accounts) ? data.accounts : [];
      setAllegroAccounts(accounts);
      setHasAllegroConnected(accounts.length > 0);
    } catch (error) {
      setIntegrationMessage({
        kind: "error",
        text: error.message || "Unable to load Allegro accounts.",
      });
    } finally {
      setAccountsLoading(false);
    }
  }, [authFetch, authToken, setHasAllegroConnected]);

  const toggleNotification = (field) => (event) => {
    const { checked } = event.target;
    setNotifications((prev) => ({ ...prev, [field]: checked }));
  };

  useEffect(() => {
    fetchAllegroAccounts();
  }, [fetchAllegroAccounts]);

  const handleNotificationsSubmit = async (event) => {
    event.preventDefault();
    setSavingNotifications(true);
    setNotificationMessage(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 450));
      setNotificationMessage({
        kind: "success",
        text: "Notification preferences saved. Replace this stub with a real API call when ready.",
      });
    } catch (error) {
      setNotificationMessage({
        kind: "error",
        text: error.message || "Unable to update notifications.",
      });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({
        kind: "error",
        text: "All password fields are required before submitting.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        kind: "error",
        text: "New passwords do not match. Please try again.",
      });
      return;
    }

    setSavingPassword(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 600));
      setPasswordMessage({
        kind: "success",
        text: "Password updated locally. Connect to your change-password endpoint to persist.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setPasswordMessage({
        kind: "error",
        text: error.message || "Unable to change password.",
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAllegroConnect = async () => {
    if (!authToken) {
      setIntegrationMessage({
        kind: "error",
        text: "You need to be signed in to connect Allegro.",
      });
      return;
    }
    setIntegrationLoading(true);
    setIntegrationMessage(null);
    try {
      const resp = await authFetch(`${API_BASE_URL}/api/allegro/initiate/`, {
        headers: { Accept: "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data.message || data.detail || `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        throw new Error("Authorization URL was not returned by the server.");
      }
    } catch (error) {
      setIntegrationMessage({
        kind: "error",
        text: error.message || "Unable to start the Allegro connection flow.",
      });
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleAllegroDisconnect = async (accountId) => {
    if (!authToken) {
      setIntegrationMessage({
        kind: "error",
        text: "You need to be signed in to disconnect Allegro.",
      });
      return;
    }
    if (typeof window !== "undefined" && !window.confirm("Disconnect this Allegro account?")) {
      return;
    }
    setDisconnectingAccountId(accountId);
    setIntegrationMessage(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/allegro/disconnect/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${AUTH_SCHEME} ${authToken}`,
        },
        body: JSON.stringify({ account_id: accountId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data.detail || data.message || data.error || `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      setIntegrationMessage({
        kind: "success",
        text: data.message || "Allegro account disconnected.",
      });
      await fetchAllegroAccounts();
    } catch (error) {
      setIntegrationMessage({
        kind: "error",
        text: error.message || "Unable to disconnect Allegro right now.",
      });
    } finally {
      setDisconnectingAccountId(null);
    }
  };

  const handleSetPrimaryAccount = async (accountId) => {
    if (!authToken) {
      setIntegrationMessage({
        kind: "error",
        text: "You need to be signed in to manage Allegro accounts.",
      });
      return;
    }
    setPrimaryUpdatingId(accountId);
    setIntegrationMessage(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/allegro/accounts/${accountId}/primary/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${AUTH_SCHEME} ${authToken}`,
        },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data.detail || data.message || `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      setIntegrationMessage({
        kind: "success",
        text: data.detail || "Active Allegro account updated.",
      });
      await fetchAllegroAccounts();
    } catch (error) {
      setIntegrationMessage({
        kind: "error",
        text: error.message || "Unable to update the primary Allegro account.",
      });
    } finally {
      setPrimaryUpdatingId(null);
    }
  };

  const handleAllegroDataImport = async () => {
    if (!authToken) {
      setImportMessage({
        kind: "error",
        text: "You need to be signed in to import Allegro data.",
      });
      return;
    }

    setImportingData(true);
    setImportMessage(null);

    try {
      const successes = [];
      const warnings = [];
      const failures = [];

      const coerceNumber = (value) => {
        if (value === undefined || value === null) return null;
        const numeric = typeof value === "string" ? Number(value) : value;
        return Number.isFinite(numeric) ? numeric : null;
      };

      const recordResult = (label, data) => {
        if (!data || typeof data !== "object") {
          successes.push(`${label} import completed.`);
          return;
        }

        const created = coerceNumber(
          data.created ?? data.created_count ?? data.imported ?? data.new ?? data.added,
        );
        const updated = coerceNumber(
          data.updated ?? data.updated_count ?? data.modified ?? data.changed,
        );
        const processed = coerceNumber(
          data.processed ?? data.count ?? data.total ?? data.records ?? data.entries,
        );

        const parts = [];
        if (created !== null) parts.push(`created ${created}`);
        if (updated !== null) parts.push(`updated ${updated}`);
        if (!parts.length && processed !== null) parts.push(`processed ${processed}`);

        successes.push(
          parts.length ? `${label}: ${parts.join(", ")}` : `${label} import completed.`,
        );

        const explicitErrorCount = Array.isArray(data.errors) ? data.errors.length : 0;
        const numericErrorCount =
          explicitErrorCount ||
          coerceNumber(
            data.error_count ?? data.errors_count ?? data.failed ?? data.failures ?? null,
          );

        if (numericErrorCount) {
          warnings.push(
            `${label} reported ${numericErrorCount} issue${numericErrorCount === 1 ? "" : "s"}.`,
          );
        }
      };

      const emitRefreshEvent = (name, detail) => {
        if (typeof window === "undefined") return;
        window.dispatchEvent(detail ? new CustomEvent(name, { detail }) : new Event(name));
      };

      try {
        const products = await apiFetch(`${API_BASE_URL}/api/allegro/products/import/`, {
          method: "POST",
          token: authToken,
          body: {},
        });
        recordResult("Offers", products);
        emitRefreshEvent("allegro:offers-imported", { data: products });
      } catch (error) {
        failures.push(`Offers import failed: ${error.message || "Unknown error."}`);
      }

      try {
        const orders = await apiFetch(`${API_BASE_URL}/api/orders/import-orders/`, {
          method: "POST",
          token: authToken,
          body: {},
        });
        recordResult("Orders", orders);
        emitRefreshEvent("allegro:orders-imported", { data: orders });
      } catch (error) {
        failures.push(`Orders import failed: ${error.message || "Unknown error."}`);
      }

      try {
        const billing = await apiFetch(`${API_BASE_URL}/api/billing/import/`, {
          method: "POST",
          token: authToken,
          body: {},
        });
        recordResult("Billing", billing);
        emitRefreshEvent("billing:refresh");
      } catch (error) {
        failures.push(`Billing import failed: ${error.message || "Unknown error."}`);
      }

      const summaryParts = [];
      if (successes.length) summaryParts.push(successes.join(" • "));
      if (warnings.length) summaryParts.push(warnings.join(" "));
      if (failures.length) summaryParts.push(failures.join(" "));

      const hasFailures = failures.length > 0;
      const hasWarnings = warnings.length > 0;
      let kind = "success";
      if (hasFailures) {
        kind = successes.length ? "info" : "error";
      } else if (hasWarnings) {
        kind = "info";
      }

      setImportMessage({
        kind,
        text: summaryParts.join(" ") || "Allegro import completed.",
      });
    } finally {
      setImportingData(false);
    }
  };

  return (
    <div className="space-y-10">
      <header className="flex items-start gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-900/20">
          <ShieldCheck size={32} />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold text-slate-900">Account settings</h2>
          <p className="text-sm text-slate-500">
            Manage how Allegro Project keeps your account secure and how you receive alerts.
          </p>
        </div>
      </header>

      <section className="space-y-4 rounded-3xl border border-white/30 bg-white/95 p-6 shadow-inner shadow-white/40">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Allegro integration</h3>
            <p className="text-sm text-slate-500">
              Control the connection between Allegro and your workspace. Disconnecting stops order syncs immediately.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div
              className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm ${
                hasAllegroConnected
                  ? "border-emerald-200 bg-emerald-50/80 text-emerald-700"
                  : "border-slate-200 bg-white/80 text-slate-600"
              }`}
            >
              {hasAllegroConnected ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <LinkIcon className="h-4 w-4" />
              )}
              <span>
                {hasAllegroConnected ? "At least one Allegro account is connected." : "No Allegro connections yet."}
              </span>
            </div>
            <button
              type="button"
              onClick={handleAllegroConnect}
              disabled={integrationLoading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-900/20 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:bg-indigo-400"
            >
              {integrationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
              {integrationLoading ? "Opening Allegro…" : "Connect Allegro account"}
            </button>
          </div>
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 text-sm text-slate-700 shadow-inner shadow-white/30">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Connected accounts</h4>
            <p className="text-sm text-slate-500">
              Set which Allegro account is active, or disconnect the ones you no longer need.
            </p>
          </div>
          {accountsLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading Allegro accounts…
            </div>
          ) : allegroAccounts.length ? (
            <div className="space-y-3">
              {allegroAccounts.map((account) => (
                <div
                  key={account.id}
                  className="space-y-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-700 shadow-inner shadow-white/40"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-slate-900">
                          {resolveAccountLabel(account)}
                        </span>
                        {account.is_default && (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                            Active
                          </span>
                        )}
                      </div>
                      {account.allegro_login && (
                        <p className="text-sm text-slate-500">Login: {account.allegro_login}</p>
                      )}
                      <p className="text-xs text-slate-400">
                        Expires: {formatTimestamp(account.expires_at)}
                        {account.last_used ? ` · Last used ${formatTimestamp(account.last_used)}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!account.is_default && (
                      <button
                        type="button"
                        onClick={() => handleSetPrimaryAccount(account.id)}
                        disabled={primaryUpdatingId === account.id}
                        className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {primaryUpdatingId === account.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        {primaryUpdatingId === account.id ? "Updating…" : "Set active"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAllegroDisconnect(account.id)}
                      disabled={disconnectingAccountId === account.id}
                      className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {disconnectingAccountId === account.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <AlertTriangle className="h-4 w-4" />
                      )}
                      {disconnectingAccountId === account.id ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-500">
              No Allegro accounts connected yet. Use the button above to add your first account.
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4 text-sm text-slate-700 shadow-inner shadow-white/30">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900">Import data from Allegro</h4>
              <p className="text-sm text-slate-500">
                Pull the latest offers, orders, and billing data in a single run. Use refresh controls in each view to see updates.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAllegroDataImport}
              disabled={importingData || integrationLoading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:bg-slate-600/70"
            >
              {importingData ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {importingData ? "Importing…" : "Import Allegro data"}
            </button>
          </div>
          {importMessage && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                importMessage.kind === "error"
                  ? "border-rose-200 bg-rose-50/80 text-rose-700"
                  : importMessage.kind === "info"
                  ? "border-amber-200 bg-amber-50/80 text-amber-700"
                  : "border-emerald-200 bg-emerald-50/80 text-emerald-700"
              }`}
            >
              {importMessage.text}
            </div>
          )}
        </div>

        {integrationMessage && (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              integrationMessage.kind === "error"
                ? "border-rose-200 bg-rose-50/80 text-rose-700"
                : "border-emerald-200 bg-emerald-50/80 text-emerald-700"
            }`}
          >
            {integrationMessage.text}
          </div>
        )}
      </section>

      <section className="space-y-6 rounded-3xl border border-white/30 bg-white/95 p-6 shadow-inner shadow-white/40">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-600 shadow-inner shadow-white/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Security tip: enable two-factor authentication once it is available to keep orders safe.
        </div>

        <form onSubmit={handleNotificationsSubmit} className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Email notifications</h3>
            <p className="text-sm text-slate-500">
              Choose what lands in your inbox. These settings affect order alerts and billing updates.
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-700 shadow-inner shadow-white/30">
              <input
                type="checkbox"
                checked={notifications.emailOrders}
                onChange={toggleNotification("emailOrders")}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
              />
              <span>
                <span className="font-medium text-slate-900">Order activity</span>
                <br />
                <span className="text-slate-500">Get notified when new orders come in or change state.</span>
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-700 shadow-inner shadow-white/30">
              <input
                type="checkbox"
                checked={notifications.emailAlerts}
                onChange={toggleNotification("emailAlerts")}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
              />
              <span>
                <span className="font-medium text-slate-900">Critical alerts</span>
                <br />
                <span className="text-slate-500">Alerts about Allegro sync issues or failed uploads.</span>
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-700 shadow-inner shadow-white/30">
              <input
                type="checkbox"
                checked={notifications.weeklySummary}
                onChange={toggleNotification("weeklySummary")}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
              />
              <span>
                <span className="font-medium text-slate-900">Weekly summary</span>
                <br />
                <span className="text-slate-500">Digest of revenue, profit, and inventory highlights.</span>
              </span>
            </label>
          </div>

          {notificationMessage && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                notificationMessage.kind === "error"
                  ? "border-rose-200 bg-rose-50/80 text-rose-700"
                  : "border-emerald-200 bg-emerald-50/80 text-emerald-700"
              }`}
            >
              {notificationMessage.text}
            </div>
          )}

          <button
            type="submit"
            disabled={savingNotifications}
            className={`inline-flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-900/20 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-white ${
              savingNotifications ? "cursor-wait bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-500"
            }`}
          >
            {savingNotifications ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MailCheck className="h-4 w-4" />
            )}
            {savingNotifications ? "Saving…" : "Save notification preferences"}
          </button>
        </form>
      </section>

      <section className="space-y-6 rounded-3xl border border-white/30 bg-white/95 p-6 shadow-inner shadow-white/40">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Change password</h3>
          <p className="text-sm text-slate-500">
            Password changes will log you out of other sessions to keep your Allegro access secure.
          </p>
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            Current password
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm text-slate-600">
              New password
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </label>
            <label className="flex flex-col gap-2 text-sm text-slate-600">
              Confirm password
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </label>
          </div>

          {passwordMessage && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                passwordMessage.kind === "error"
                  ? "border-rose-200 bg-rose-50/80 text-rose-700"
                  : "border-emerald-200 bg-emerald-50/80 text-emerald-700"
              }`}
            >
              {passwordMessage.text}
            </div>
          )}

          <button
            type="submit"
            disabled={savingPassword}
            className={`inline-flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white ${
              savingPassword ? "cursor-wait bg-slate-400/80" : "bg-slate-900 hover:bg-slate-800"
            }`}
          >
            {savingPassword ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {savingPassword ? "Updating…" : "Update password"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default AccountSettings;
